import { useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Search } from "lucide-react";
import { HealthKpiStatus } from "../../../../helpers/utils";
import { BudgetChart } from "../../../../components/charts/DashboardCharts";
import { getAccountCurrency } from "../../../../utils/systemSettings";
import EntityNameLink from "./EntityNameLink";
import KpiInfoIcon from "./KpiInfoIcon";
import GaugeChart from "./GaugeChart";
import {
  buildStatusSegments,
  buildRiskSeverityRows,
  formatCompactMoney,
  STATUS_MAP,
  RISK_SEVERITY_LABELS,
  RISK_SEVERITY_COLORS,
  KPI_INFO,
} from "./kpiHelpers";
import styles from "../../styles/portfolioManagerDashboard.module.scss";

// Only Client/Portfolio/Program/Project groups map to a real overview page - Industry has no
// entity of its own to navigate to, and "None" is a single unlabeled group.
const NAV_ENTITY_TYPE = {
  Client: "Clients",
  Portfolio: "Portfolios",
  Program: "Programs",
  Project: "Projects",
};

const STATUS_FILTERS = [{ value: null, label: "All" }, ...HealthKpiStatus.map((s) => ({ value: s.value, label: s.name }))];

const statusLabel = (status) => HealthKpiStatus.find((s) => s.value === status)?.name ?? "On Target";
const statusColor = (status) => HealthKpiStatus.find((s) => s.value === status)?.color ?? HealthKpiStatus[0].color;

const settings = JSON.parse(localStorage.getItem("settings"));
const currency = getAccountCurrency(settings);
const formatMoney = (val) => formatCompactMoney(settings, currency, val);

const isSameGroup = (a, b) => a && b && a.groupType === b.groupType && a.id === b.id;

function GroupKpiCard({ row, selected, onSelectGroup }) {
  const entityType = NAV_ENTITY_TYPE[row.groupType];
  const groupRef = row.group?.id ? { groupType: row.groupType, id: row.group.id, name: row.group.name } : null;
  const health = statusColor(row.health?.current ?? 0);
  const statusSegments = buildStatusSegments(row.projectsByStatus);
  const riskSegments = buildRiskSeverityRows(row.risksBySeverity).map((r) => ({
    name: r.label,
    color: r.color,
    value: r.count,
    range: r.pct,
  }));
  // Actual is cumulative through the selected period (server-side) - the last entry is the
  // running total "delivered so far", which is all a small card has room to show meaningfully.
  const valueSoFar = row.valueDelivered?.length > 0 ? row.valueDelivered[row.valueDelivered.length - 1].actual : null;
  const progress = row.overallProgress ?? 0;

  return (
    <div
      className={`${styles.groupKpiCard} ${selected ? styles.groupKpiCardSelected : ""}`}
      style={{ "--group-health-tint": `${health}14` }}
      onClick={() => groupRef && onSelectGroup?.(selected ? null : groupRef)}
    >
      <div className={styles.groupKpiCardHeader}>
        <EntityNameLink name={row.group?.name ?? "All"} entityType={entityType} entityId={row.group?.id} />
        <span className={styles.trendBadge} style={{ color: health, background: `${health}1A` }}>
          {statusLabel(row.health?.current ?? 0)}
        </span>
      </div>

      <div className={styles.groupKpiRow}>
        <span className={styles.groupKpiRowLabel}>
          Progress <KpiInfoIcon content={KPI_INFO.overallProgress} inline />
        </span>
        <span className={styles.groupKpiValue}>{progress}%</span>
      </div>
      <div className={styles.gaugeSection} style={{ marginBottom: 4 }}>
        <div className={styles.gaugeTrack}>
          <div className={styles.gaugeFill} style={{ width: `${progress}%`, background: health }} />
        </div>
      </div>

      <div className={styles.groupKpiRow}>
        <span className={styles.groupKpiRowLabel}>
          Active projects <KpiInfoIcon content={KPI_INFO.activeProjects} inline />
        </span>
        <span className={styles.groupKpiValue}>{row.activeProjects} / {row.totalProjects}</span>
      </div>
      <div className={styles.groupKpiRow}>
        <span className={styles.groupKpiRowLabel}>
          On-time delivery <KpiInfoIcon content={KPI_INFO.onTimeDelivery} inline />
        </span>
        <span className={styles.groupKpiValue}>{Math.round((row.onTimeDelivery ?? 0) * 100)}%</span>
      </div>
      <div className={styles.groupKpiRow}>
        <span className={styles.groupKpiRowLabel}>
          Gross margin <KpiInfoIcon content={KPI_INFO.grossMargin} inline />
        </span>
        <span className={styles.groupKpiValue}>{row.costsSummary?.grossMarginPercentage ?? 0}%</span>
      </div>
      <div className={styles.groupKpiRow}>
        <span className={styles.groupKpiRowLabel}>
          At risk <KpiInfoIcon content={KPI_INFO.atRisk} inline />
        </span>
        <span className={styles.groupKpiValue}>{row.projectsAtRiskCurrent}</span>
      </div>
      {valueSoFar !== null && (
        <div className={styles.groupKpiRow}>
          <span className={styles.groupKpiRowLabel}>
            Value delivered so far <KpiInfoIcon content={KPI_INFO.valueDeliveredSoFar} inline />
          </span>
          <span className={styles.groupKpiValue}>{formatMoney(valueSoFar)}</span>
        </div>
      )}

      <hr className={styles.groupKpiDivider} />

      <div className={styles.groupKpiSectionLabel}>
        Budget <KpiInfoIcon content={KPI_INFO.budget} inline />
      </div>
      <div className={styles.groupKpiBudgetChart}>
        <BudgetChart budgetSummary={row.costsSummary} currencySymbol={currency} className="overview-budget-chart" />
      </div>

      <hr className={styles.groupKpiDivider} />

      <div className={styles.groupKpiPieRow}>
        <div className={styles.groupKpiPieCol}>
          <div className={styles.groupKpiSectionLabel}>
            Project Statuses <KpiInfoIcon content={KPI_INFO.projectsByStatus} inline />
          </div>
          <GaugeChart segments={statusSegments} size={90} showLegend={false} />
        </div>
        <div className={styles.groupKpiPieCol}>
          <div className={styles.groupKpiSectionLabel}>
            Risk <KpiInfoIcon content={KPI_INFO.risk} inline />
          </div>
          <GaugeChart segments={riskSegments} size={90} showLegend={false} />
        </div>
      </div>
    </div>
  );
}

const LEGEND_TYPES = {
  status: { label: "Project Statuses", items: STATUS_MAP.map((s) => ({ name: s.name, color: s.color })) },
  risk: { label: "Risk", items: RISK_SEVERITY_LABELS.map((label, i) => ({ name: label, color: RISK_SEVERITY_COLORS[i] })) },
};

const GroupedKpiCards = ({ groups = [], loading, groupByLabel, selectedGroup, onSelectGroup }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [legendType, setLegendType] = useState("status");

  // Worst health first (Slipping, then Monitoring, then On Target) - the groups that need
  // attention should never be scrolled past.
  const visibleGroups = useMemo(() => {
    return groups
      .filter((g) => !search || (g.group?.name ?? "All").toLowerCase().includes(search.toLowerCase()))
      .filter((g) => statusFilter === null || (g.health?.current ?? 0) === statusFilter)
      .slice()
      .sort((a, b) => (b.health?.current ?? 0) - (a.health?.current ?? 0));
  }, [groups, search, statusFilter]);

  if (loading) {
    return (
      <div className={styles.groupedPanel}>
        <div className={styles.groupedPanelHeader}>Grouped by {groupByLabel}</div>
        <div className={styles.groupKpiGrid}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.groupKpiCard}>
              <Skeleton height={16} width="70%" />
              <Skeleton height={12} width="50%" style={{ marginTop: 12 }} />
              <Skeleton height={12} width="50%" style={{ marginTop: 8 }} />
              <Skeleton height={12} width="50%" style={{ marginTop: 8 }} />
              <Skeleton height={60} style={{ marginTop: 12 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!groups.length) return null;

  return (
    <div className={styles.groupedPanel}>
      <div className={styles.groupedPanelHeaderRow}>
        <div className={styles.groupedPanelLegend}>
          <span className={styles.groupedPanelHeader}>Grouped by {groupByLabel}</span>
          <select
            className={styles.metricSelect}
            value={legendType}
            onChange={(e) => setLegendType(e.target.value)}
          >
            {Object.entries(LEGEND_TYPES).map(([key, { label }]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <div className={styles.groupedLegendItems}>
            {LEGEND_TYPES[legendType].items.map((item) => (
              <span key={item.name} className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: item.color }} />
                {item.name}
              </span>
            ))}
          </div>
        </div>
        <div className={styles.groupedPanelControls}>
          <div className={styles.groupedSearchBox}>
            <Search size={13} />
            <input
              type="text"
              placeholder="Search groups"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.groupedSearchInput}
            />
          </div>
          <div className={styles.groupedStatusPills}>
            {STATUS_FILTERS.map((opt) => (
              <button
                key={opt.label}
                type="button"
                className={`${styles.groupedStatusPill} ${statusFilter === opt.value ? styles.groupedStatusPillActive : ""}`}
                onClick={() => setStatusFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {visibleGroups.length === 0 ? (
        <div className={styles.groupedEmptyState}>No groups match the current search/status filter.</div>
      ) : (
        <div className={styles.groupKpiGrid}>
          {visibleGroups.map((row) => (
            <GroupKpiCard
              key={`${row.groupType}-${row.group?.id ?? "none"}`}
              row={row}
              selected={isSameGroup(selectedGroup, { groupType: row.groupType, id: row.group?.id })}
              onSelectGroup={onSelectGroup}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupedKpiCards;
